import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LandingNav from '@/pages/home/components/LandingNav';
import { useProducts, type Product } from '@/hooks/useProducts';
import { useAuthContext } from '@/contexts/AuthContext';
import { useCheckout } from '@/hooks/useCheckout';
import { usePaymentProviders } from '@/hooks/usePaymentProviders';

const planDuration: Record<string, string> = {
  price_1_2_year: '1–2 Year',
  price_3_5_year: '3–5 Year',
  price_lifetime: 'Lifetime',
};

type PlanKey = 'price_1_2_year' | 'price_3_5_year' | 'price_lifetime';

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function ProductDetailPage() {
  const { productSlug } = useParams<{ productSlug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  const { products, loading } = useProducts();
  const { createCheckout, loading: checkoutLoading } = useCheckout();
  const { providers } = usePaymentProviders();

  const [product, setProduct] = useState<Product | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>('price_lifetime');
  const [selectedProvider, setSelectedProvider] = useState<'nowpayments' | 'stripe'>('nowpayments');
  const [checkoutError, setCheckoutError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'docs' | 'pricing'>('overview');

  const nowpaymentsActive = providers.find(p => p.provider === 'nowpayments')?.is_active ?? false;
  const stripeActive = providers.find(p => p.provider === 'stripe')?.is_active ?? false;
  const hasAnyProvider = nowpaymentsActive || stripeActive;

  useEffect(() => {
    if (!nowpaymentsActive && stripeActive) setSelectedProvider('stripe');
    else if (nowpaymentsActive) setSelectedProvider('nowpayments');
  }, [nowpaymentsActive, stripeActive]);

  useEffect(() => {
    if (!loading && products.length > 0) {
      const found = products.find(p => slugify(p.name) === productSlug);
      if (found) setProduct(found);
      else navigate('/marketplace', { replace: true });
    }
  }, [loading, products, productSlug, navigate]);

  const handleCheckout = async () => {
    if (!product) return;
    setCheckoutError('');
    if (!isAuthenticated) {
      navigate(`/login?redirect=/marketplace/${productSlug}`);
      return;
    }
    const result = await createCheckout({
      product_id: product.id,
      pricing_type: selectedPlan,
      amount: product[selectedPlan],
      provider: selectedProvider,
    });
    if (result.success) {
      const url = result.invoice_url || result.checkout_url;
      if (url) window.location.href = url;
      else setCheckoutError('Checkout created but no redirect URL returned.');
    } else {
      setCheckoutError(result.error || 'Failed to create checkout. Please try again.');
    }
  };

  if (loading || !product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LandingNav />
        <div className="pt-32 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-brand-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <LandingNav />

      {/* Hero */}
      <div className="bg-sidebar pt-24 pb-10 px-6">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/marketplace')}
            className="flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-6 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-s-line" /> Back to Marketplace
          </button>
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${product.color}25` }}>
              <i className={`${product.icon} text-3xl`} style={{ color: product.color }} />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className="text-[11px] font-bold text-brand-400 tracking-widest uppercase bg-brand-500/10 px-2.5 py-1 rounded-full">{product.category}</span>
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">{product.status}</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2">{product.name}</h1>
              <p className="text-white/60 text-sm max-w-2xl leading-relaxed">{product.description}</p>
              <div className="flex flex-wrap gap-5 mt-5">
                {[
                  { label: 'Uptime', value: product.uptime, icon: 'ri-pulse-line' },
                  { label: 'Endpoints', value: String(product.endpoints), icon: 'ri-code-s-slash-line' },
                  { label: 'Req / Month', value: `${(product.requests_per_month / 1000000).toFixed(1)}M`, icon: 'ri-database-2-line' },
                ].map(stat => (
                  <div key={stat.label} className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                      <i className={`${stat.icon} text-white/70 text-sm`} />
                    </div>
                    <div>
                      <div className="text-white font-bold text-sm">{stat.value}</div>
                      <div className="text-white/40 text-[10px]">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-white/40 text-xs">Starting from</div>
              <div className="text-3xl font-extrabold text-white">${product.price_lifetime}</div>
              <div className="text-brand-400 text-xs mt-0.5">Lifetime License</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-100 sticky top-16 z-30">
        <div className="max-w-6xl mx-auto px-6 flex gap-1">
          {(['overview', 'docs', 'pricing'] as const).map(t => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-5 py-4 text-sm font-semibold capitalize cursor-pointer whitespace-nowrap border-b-2 transition-colors ${activeTab === t ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
              <div className="bg-white rounded-2xl p-6">
                <h2 className="font-bold text-gray-900 text-lg mb-4">About this API</h2>
                <p className="text-sm text-gray-600 leading-relaxed">{product.description}</p>
                <p className="text-sm text-gray-600 leading-relaxed mt-3">
                  This enterprise-grade API provides seamless integration for your applications. With industry-leading uptime of {product.uptime},
                  {product.endpoints} endpoints, and support for up to {(product.requests_per_month / 1000000).toFixed(1)}M requests per month,
                  it&apos;s built to scale with your business.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6">
                <h3 className="font-bold text-gray-900 mb-4">Key Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { icon: 'ri-shield-check-line', label: 'Enterprise Security', desc: 'TLS encryption, OAuth2' },
                    { icon: 'ri-speed-line', label: 'Low Latency', desc: 'Average &lt;50ms response' },
                    { icon: 'ri-24-hours-line', label: '24/7 Monitoring', desc: `${product.uptime} guaranteed uptime` },
                    { icon: 'ri-code-s-slash-line', label: 'Full REST API', desc: `${product.endpoints} endpoints` },
                    { icon: 'ri-file-code-line', label: 'SDKs Available', desc: 'JS, Python, Go, PHP' },
                    { icon: 'ri-customer-service-2-line', label: 'Developer Support', desc: 'Email & ticket support' },
                  ].map(f => (
                    <div key={f.label} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center shrink-0">
                        <i className={`${f.icon} text-brand-500 text-sm`} />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-800">{f.label}</div>
                        <div className="text-[11px] text-gray-500" dangerouslySetInnerHTML={{ __html: f.desc }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'docs' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
              <div className="bg-white rounded-2xl p-6">
                <h2 className="font-bold text-gray-900 text-lg mb-4">Quick Start</h2>
                <div className="space-y-4">
                  {[
                    { step: '1', title: 'Subscribe & Get API Key', desc: 'Purchase a plan and your API key will be generated instantly in your dashboard.' },
                    { step: '2', title: 'Authenticate Requests', desc: 'Pass your API key in the Authorization header: Bearer YOUR_API_KEY' },
                    { step: '3', title: 'Make Your First Call', desc: `Send a GET request to the base endpoint to verify your connection.` },
                  ].map(s => (
                    <div key={s.step} className="flex gap-4">
                      <div className="w-7 h-7 bg-brand-500 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">{s.step}</div>
                      <div>
                        <div className="font-semibold text-sm text-gray-900">{s.title}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 bg-gray-900 rounded-xl p-4">
                  <div className="text-[11px] text-gray-500 mb-2 font-mono">Example Request</div>
                  <pre className="text-xs text-emerald-400 font-mono overflow-x-auto whitespace-pre-wrap">{`curl -X GET https://api.apimarts.com/v1/${slugify(product.name)} \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`}</pre>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'pricing' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              {(['price_1_2_year', 'price_3_5_year', 'price_lifetime'] as PlanKey[]).map(plan => (
                <div key={plan} className={`bg-white rounded-2xl p-6 border-2 cursor-pointer transition-all ${selectedPlan === plan ? 'border-brand-500' : 'border-gray-100 hover:border-gray-200'}`}
                  onClick={() => setSelectedPlan(plan)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-gray-900">{planDuration[plan]}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{plan === 'price_lifetime' ? 'Best value — never expires' : 'Instant access after payment'}</div>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">${product[plan]}</div>
                  </div>
                  {plan === 'price_lifetime' && (
                    <div className="mt-3 text-[11px] bg-brand-50 text-brand-600 px-3 py-1.5 rounded-lg inline-block font-semibold">Most Popular</div>
                  )}
                </div>
              ))}
            </motion.div>
          )}
        </div>

        {/* Sidebar purchase card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl p-5 sticky top-28 space-y-4">
            <div className="text-center pb-4 border-b border-gray-100">
              <div className="text-xs text-gray-400 mb-1">Selected Plan</div>
              <div className="text-2xl font-extrabold text-gray-900">${product[selectedPlan]}</div>
              <div className="text-xs text-brand-600 font-semibold mt-0.5">{planDuration[selectedPlan]} License</div>
            </div>

            {/* Plan pills */}
            <div className="space-y-2">
              {(['price_1_2_year', 'price_3_5_year', 'price_lifetime'] as PlanKey[]).map(plan => (
                <button
                  key={plan}
                  onClick={() => setSelectedPlan(plan)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border cursor-pointer whitespace-nowrap transition-all text-sm ${selectedPlan === plan ? 'border-brand-500 bg-brand-50/40 text-brand-700 font-semibold' : 'border-gray-100 text-gray-600 hover:border-gray-200'}`}
                >
                  <span>{planDuration[plan]}</span>
                  <span className="font-bold">${product[plan]}</span>
                </button>
              ))}
            </div>

            {/* Provider */}
            <div>
              <div className="text-xs text-gray-500 mb-2 font-medium">Pay With</div>
              <div className="flex gap-2 flex-wrap">
                {nowpaymentsActive && (
                  <button onClick={() => setSelectedProvider('nowpayments')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border cursor-pointer whitespace-nowrap transition-all ${selectedProvider === 'nowpayments' ? 'bg-sidebar text-white border-sidebar' : 'border-gray-200 text-gray-600'}`}>
                    <i className="ri-coin-line" />Crypto
                  </button>
                )}
                {stripeActive && (
                  <button onClick={() => setSelectedProvider('stripe')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border cursor-pointer whitespace-nowrap transition-all ${selectedProvider === 'stripe' ? 'bg-sidebar text-white border-sidebar' : 'border-gray-200 text-gray-600'}`}>
                    <i className="ri-bank-card-line" />Card
                  </button>
                )}
              </div>
            </div>

            <AnimatePresence>
              {checkoutError && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
                  <i className="ri-error-warning-line mr-1" />{checkoutError}
                </motion.div>
              )}
            </AnimatePresence>

            <button
              onClick={handleCheckout}
              disabled={checkoutLoading || !hasAnyProvider}
              className="w-full py-3.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-bold rounded-xl cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2 text-sm"
            >
              {checkoutLoading ? <><i className="ri-loader-4-line animate-spin" />Processing…</> :
                !isAuthenticated ? <><i className="ri-login-circle-line" />Sign In to Purchase</> :
                <><i className="ri-secure-payment-line" />Buy Now — ${product[selectedPlan]}</>}
            </button>
            <div className="flex items-center justify-center gap-2 text-[11px] text-gray-400">
              <i className="ri-shield-check-line text-green-500" />
              Secure payment · Instant access
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
